import { Component, inject, signal, computed, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import {
  AuthService,
  Client,
  ContextService,
  needsClientPortalSelection,
  redirectToLoginWhenSignedOut,
  User
} from '@lr/core';
import { AppSnackbarComponent, HeaderComponent, SidebarComponent } from '@lr/ui';
import { ClientSelectorComponent } from './components/client-selector/client-selector.component';
import { NavigationComponent } from './components/navigation/navigation.component';
import { filter, takeUntil, distinctUntilChanged } from 'rxjs/operators';
import { Subject, combineLatest } from 'rxjs';


@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    HeaderComponent,
    ClientSelectorComponent,
    SidebarComponent,
    NavigationComponent,
    AppSnackbarComponent
  ],
  template: `
    <div class="app-container">
      @if (showLayout()) {
        <lib-header
          [title]="'Client Portal'"
          [userName]="userName()"
          [userEmail]="userEmail()"
          [logoUrl]="'assets/legal-resources-logo.png'"
          [endOnMobile]="true"
          [userRoles]="userRoles()"
          (menuToggle)="toggleSidebar()"
          (loginClick)="login()"
          (logoutClick)="logout()"
          (profileClick)="goToProfile()"
          (settingsClick)="goToSettings()">
          <app-client-selector
            headerCenter
            [clients]="clients()"
            [selectedClientId]="selectedClientId()"
            [clientName]="selectedClient()?.name || null"
            (clientChange)="onClientChange($event)">
          </app-client-selector>
        </lib-header>

        <lib-sidebar
          [opened]="sidebarOpened()"
          [mode]="sidebarMode()"
          [hasBackdrop]="sidebarMode() === 'over'"
          (closedChange)="sidebarOpened.set($event)">
          <app-navigation
            [userName]="userName()"
            [userEmail]="userEmail()"
            [userRoles]="userRoles()"
            (logoutClick)="logout()"
            (closeMenu)="sidebarOpened.set(false)">
          </app-navigation>
          
          <div content class="main-content">
            <router-outlet></router-outlet>
          </div>
        </lib-sidebar>
      } @else {
        <div class="full-screen-content">
          <router-outlet></router-outlet>
        </div>
      }
      <lib-app-snackbar />
    </div>
  `,
  styles: [`
    .app-container {
      display: flex;
      flex-direction: column;
      height: 100vh;
    }

    lib-header {
      display: block !important;
      visibility: visible !important;
      opacity: 1 !important;
      position: fixed !important;
      top: 0 !important;
      left: 0 !important;
      right: 0 !important;
      z-index: 100 !important; /* Fixed at 100 */
      width: 100% !important;
      background-color: #f2f5f9 !important;
    }

    /* Ensure header is visible on all screen sizes */
    @media (max-width: 1024px) {
      lib-header {
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
        position: fixed !important;
        top: 0 !important;
        left: 0 !important;
        right: 0 !important;
        z-index: 100 !important;
        width: 100% !important;
        background-color: #f2f5f9 !important;
      }
    }

    @media (min-width: 1025px) {
      lib-header {
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
        position: fixed !important;
        top: 0 !important;
        left: 0 !important;
        right: 0 !important;
        z-index: 100 !important;
        width: 100% !important;
        background-color: #f2f5f9 !important;
      }
    }

    .main-content {
      padding: 24px;
      min-height: 100%;
      z-index: 1; /* Below header */
      position: relative;
    }

    @media (max-width: 768px) {
      .main-content {
        padding: 16px;
      }
    }

    .full-screen-content {
      width: 100%;
      height: 100vh;
      overflow: auto;
    }
  `]
})
export class AppComponent implements OnInit, OnDestroy {
  private authService = inject(AuthService);
  private contextService = inject(ContextService);
  private router = inject(Router);
  private destroy$ = new Subject<void>();

  sidebarOpened = signal(true);
  sidebarMode = signal<'side' | 'over' | 'push'>('side');
  userName = signal<string | null>(null);
  userEmail = signal<string | null>(null);
  /** All roles for the active portal (for header / sidebar display). */
  userRoles = signal<string[]>([]);
  selectedClientId = signal<string | null>(null);
  currentRoute = signal<string>('');
  currentUser = signal<User | null | undefined>(null);

  // Routes that should NOT show header/sidebar (public/auth pages)
  private publicRoutes = ['/callback', '/claims-loading', '/logout', '/access-denied', '/unauthorized', '/client-selection', '/notification-setup', '/login', '/no-group-access'];
 
  // Computed property to determine if layout should be shown
  // If we're on a protected route, the guard already verified authentication
  // So we just need to check if we have a user and it's not a public route
  showLayout = computed(() => {
    const route = this.currentRoute();
    const user = this.currentUser();
    
    // Don't show layout if no user
    if (!user) {
      return false;
    }
    
    // Don't show layout on public routes
    const isPublicRoute = this.publicRoutes.some(publicRoute => {
      // Remove query params and hash for comparison
      const cleanRoute = route.split('?')[0].split('#')[0];
      return cleanRoute === publicRoute || cleanRoute.startsWith(publicRoute + '/');
    });
    
    if (isPublicRoute) {
      return false;
    }
    
    // Show layout for protected routes (guard already verified authentication)
    return true;
  });
  
  // Clients list - updated reactively when permissions change (computed has no signal deps on BehaviorSubject)
  clients = signal<Client[]>([]);

  selectedClient = computed(() => {
    return this.contextService.getSelectedClient();
  });
  private resizeHandler = () => this.handleResize();

  constructor() {
    // Subscribe to user changes for user data
    // The guard handles authentication verification - we just need the user data for display
    this.authService.user$.pipe(
      takeUntil(this.destroy$),
      distinctUntilChanged((prev, curr) => {
        // Compare user IDs to prevent duplicate processing
        return prev?.sub === curr?.sub;
      })
    ).subscribe(user => {
      if (user) {
        this.currentUser.set(user);
        this.userName.set(user?.name || user?.email || null);
        this.userEmail.set(user?.email || null);
        // Don't set role here - it will be set when context is ready
        
        // Set default selected client if none is selected
        const clientsList = this.clients();
        const currentSelectedClient = this.contextService.getSelectedClient();
        
        if (!currentSelectedClient && clientsList.length > 0) {
          const firstClient = clientsList[0];
          this.contextService.setSelectedClient(firstClient.mCode);
        }
      } else {
        this.currentUser.set(null);
        this.userName.set(null);
        this.userEmail.set(null);
        this.userRoles.set([]);
      }
    });

    // Subscribe to selected client changes to keep selectedClientId in sync
    this.contextService.selectedClient$.pipe(
      takeUntil(this.destroy$),
      distinctUntilChanged((prev, curr) => prev?.mCode === curr?.mCode)
    ).subscribe(client => {
      this.selectedClientId.set(client?.mCode || null);
    });

    // Subscribe to permissions changes: sync clients list and set default selected client when permissions are loaded
    this.contextService.permissions$.pipe(
      takeUntil(this.destroy$),
      filter(permissions => permissions !== null)
    ).subscribe(() => {
      const clientsList = this.contextService.getClients();
      this.clients.set(clientsList);

      const currentSelectedClient = this.contextService.getSelectedClient();
      if (!currentSelectedClient && clientsList.length > 0) {
        const firstClient = clientsList[0];
        this.contextService.setSelectedClient(firstClient.mCode);
      }
    });

    // Subscribe to context ready state: sync clients, conditional auto-select, update role
    this.contextService.contextReady$.pipe(
      takeUntil(this.destroy$),
      filter(ready => ready === true), // Only proceed when context is fully ready
      distinctUntilChanged() // Only emit when ready state changes
    ).subscribe(() => {
      console.log('AppComponent: Context ready, syncing clients and updating role');
      const clientsList = this.contextService.getClients();
      this.clients.set(clientsList);
      const roles = this.contextService.getRoles();

      // Only auto-select when single client and no client selection required
      if (!this.selectedClientId() && clientsList.length > 0 && !needsClientPortalSelection(roles, clientsList.length)) {
        const firstClient = clientsList[0];
        this.selectedClientId.set(firstClient.mCode);
        this.contextService.setSelectedClient(firstClient.mCode);
      }

      this.updateUserRole();
    });

    // Sync selectedClientId when ClientContextService selection changes (e.g. from client-selection page)
    this.contextService.selectedClient$.pipe(
      takeUntil(this.destroy$),
      filter((client): client is Client => client !== null)
    ).subscribe((client) => {
      if (client.mCode !== this.selectedClientId()) {
        this.selectedClientId.set(client.mCode);
      }
    });

    // Also subscribe to individual changes in case contextReady doesn't fire
    combineLatest([
      this.contextService.context$,
      this.contextService.activePortal$
    ]).pipe(
      takeUntil(this.destroy$),
      filter(([context, portal]) => context !== null && portal !== null), // Only proceed when both are available
      distinctUntilChanged((prev, curr) => {
        // Only emit if context or portal actually changed
        const prevContextId = prev[0]?.userId;
        const currContextId = curr[0]?.userId;
        return prevContextId === currContextId && prev[1] === curr[1];
      })
    ).subscribe(([context, portal]) => {
      console.log('AppComponent: Context and portal available, updating role', { 
        context: context?.portals?.map(p => ({ name: p.name, roles: p.roles })), 
        portal,
        roles: this.contextService.getRoles()
      });
      this.updateUserRole();
    });

    // Also check immediately if both are already available (for page refreshes or when navigating from client-selection)
    const currentContext = this.contextService.getContext();
    const currentPortal = this.contextService.getActivePortal();
    const initialClients = this.contextService.getClients();
    if (initialClients.length > 0) {
      this.clients.set(initialClients);
    }
    if (currentContext && currentPortal) {
      console.log('AppComponent: Context and portal already available on init, updating role', { 
        currentContext: currentContext.portals, 
        currentPortal,
        roles: this.contextService.getRoles()
      });
      this.updateUserRole();
    }

    // Handle responsive sidebar
    this.handleResize();
    window.addEventListener('resize', this.resizeHandler);
  }

  /**
   * Update displayed roles from context when context and active portal are available
   */
  private updateUserRole(): void {
    const context = this.contextService.getContext();
    const activePortal = this.contextService.getActivePortal();
    const roles = this.contextService.getRoles();
    
    console.log('AppComponent: updateUserRole called', {
      hasContext: !!context,
      activePortal,
      roles,
      portals: context?.portals?.map(p => ({ name: p.name, roles: p.roles })),
      allPortals: context?.portals
    });
    
    // Debug: Check if portal name matches
    if (context && activePortal) {
      const matchingPortal = context.portals.find(p => p.name === activePortal);
      console.log('AppComponent: Portal matching check', {
        activePortal,
        matchingPortal,
        allPortalNames: context.portals.map(p => p.name)
      });
    }
    
    console.log('AppComponent: Setting user roles for display:', roles);
    this.userRoles.set([...roles]);
  }

  ngOnInit() {
    redirectToLoginWhenSignedOut(this.authService, this.router, this.destroy$);

    // Track route changes
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe((event: NavigationEnd) => {
      console.log('Router: NavigationEnd to:', event.url);
      this.currentRoute.set(event.url);
    });

    // Track all router events for debugging
    this.router.events.pipe(
      takeUntil(this.destroy$)
    ).subscribe(event => {
      console.log('Router: Event:', event.constructor.name, event);
    });

    // Set initial route
    console.log('Router: Initial route:', this.router.url);
    this.currentRoute.set(this.router.url);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    window.removeEventListener('resize', this.resizeHandler);
  }

  onClientChange(mCode: string): void {
    const client = this.clients().find(c => c.mCode === mCode);
    if (!client) return;

    this.contextService.setSelectedClient(mCode);

    const currentUrl = this.router.url;
    const currentPath = currentUrl.split('?')[0].split('#')[0];
    const isInvoiceDetailRoute = /^\/billing\/invoices\/[^/]+$/.test(currentPath);

    if (isInvoiceDetailRoute) {
      void this.router.navigate(['/billing/invoices']);
      return;
    }

    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigate([currentUrl]);
    });
  }

  toggleSidebar(): void {
    this.sidebarOpened.update(opened => !opened);
  }

  login(): void {
    this.authService.login();
  }

  logout(): void {
    // Navigate to logout route first to destroy current component (e.g., dashboard)
    // The logout component will auto-trigger authService.logout() on init
    this.router.navigate(['/logout']);
  }

  goToProfile(): void {
    console.log('Navigate to profile');
  }

  goToSettings(): void {
    console.log('Navigate to settings');
  }

  private handleResize(): void {
    if (window.innerWidth <= 1024) {
      this.sidebarMode.set('over');
      this.sidebarOpened.set(false);
    } else {
      this.sidebarMode.set('side');
      this.sidebarOpened.set(true);
    }
  }
}


